import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const usePostForm = () => {
  const [post, setPost] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: '',
    images: [],
    status: 'draft',
    category: 'BLOG',
    author: '',
  });
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]); // For MultiImageUpload component
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      fetchPost();
    }
    checkUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) navigate('/admin/login');
  };

  const fetchPost = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setPost(data);
      
      // Load existing images into the images state for MultiImageUpload
      if (data.images && Array.isArray(data.images) && data.images.length > 0) {
        setImages(data.images.map((img, index) => ({
          id: `existing-${index}`,
          url: img.url,
          alt: img.alt || '',
          position: img.position || 'top',
          isNew: false
        })));
      } else if (data.image_url) {
        // Fallback to single image_url for backward compatibility
        setImages([{
          id: 'existing-0',
          url: data.image_url,
          alt: data.title,
          position: 'top',
          isNew: false
        }]);
      }
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  };

  const handleSubmit = async (status) => {
    try {
      setLoading(true);
      
      const postData = { 
        ...post,
        status: status // Use passed status value
      };
      if (!id || !postData.slug) {
        postData.slug = generateSlug(postData.title);
        
        // Add timestamp to ensure uniqueness
        if (!id) {
          postData.slug = `${postData.slug}-${Date.now().toString().slice(-6)}`;
        }
      }
      
      // Process multiple images
      const processedImages = [];
      
      for (const image of images) {
        if (image.isNew && image.file) {
          // Upload new image to storage
          const fileExt = image.file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
          const filePath = `blog/${fileName}`;
          
          const { error: uploadError } = await supabase.storage
            .from('postimages')
            .upload(filePath, image.file);
            
          if (uploadError) throw uploadError;
          
          // Get public URL for the uploaded image
          const { data: { publicUrl } } = supabase.storage
            .from('postimages')
            .getPublicUrl(filePath);
            
          processedImages.push({
            url: publicUrl,
            alt: image.alt || '',
            position: image.position || 'top'
          });
        } else {
          // Keep existing image
          processedImages.push({
            url: image.url,
            alt: image.alt || '',
            position: image.position || 'top'
          });
        }
      }
      
      // Set images array
      postData.images = processedImages;
      
      // Set first image as image_url for backward compatibility
      if (processedImages.length > 0) {
        const topImage = processedImages.find(img => img.position === 'top') || processedImages[0];
        postData.image_url = topImage.url;
      }
      
      if (id) {
        await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', id);
      } else {
        await supabase
          .from('blog_posts')
          .insert([postData]);
      }
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error saving post:', error);
      alert(`Error saving post: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleImagesChange = (newImages) => {
    setImages(newImages);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const handleContentChange = (content) => {
    setPost({ ...post, content });
  };

  const handleBackToDashboard = () => {
    navigate('/admin/dashboard');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPost({ ...post, [name]: value });
  };

  return {
    post,
    setPost,
    loading,
    images,
    id,
    handleSubmit,
    handleImagesChange,
    handleLogout,
    handleContentChange,
    handleBackToDashboard,
    handleInputChange
  };
};

export default usePostForm;
